import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon,
  ContactMail as ContactMailIcon,
  Payment as PaymentIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  MonetizationOn as MonetizationOnIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import CRM images
import salesGraphImage from '../assests/crm-page/sales-graph.png';
import recentPaymentsImage from '../assests/crm-page/recent-payments.png';
import outstandingOverdueImage from '../assests/crm-page/outstanding-overdue-status.png';
import invoicePageImage from '../assests/crm-page/invoice-page.png';
import invoiceFormImage from '../assests/crm-page/invoice-form.png';
import customerDirectoryImage from '../assests/crm-page/customer-directory.png';

const CRMLandingPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #059669, #10b981)',
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #dc2626, #ef4444)',
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Transform Your Business with{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#059669',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Smart CRM
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Manage customers, track sales, and grow your business with our comprehensive CRM solution!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#059669',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#047857'
              }
            }}
          >
            Start Growing Today
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Complete setup in minutes - Start managing your customers immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{ 
      width: '100%', 
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -20 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'Customer Management',
          'Sales Tracking',
          'Invoice Creation',
          'Revenue Analytics',
          'Customer Relations',
          'Payment Processing',
          'Sales Reporting',
          'Customer Support',
          'Lead Generation',
          'Account Management',
          'Sales Pipeline',
          'Customer Insights',
          'Revenue Growth',
          'Sales Automation',
          'Customer Database',
          'Business Intelligence',
          'Sales Forecasting',
          'Customer Retention',
          'Customer Management',
          'Sales Tracking',
          'Invoice Creation',
          'Revenue Analytics',
          'Customer Relations',
          'Payment Processing',
          'Sales Reporting',
          'Customer Support',
          'Lead Generation',
          'Account Management',
          'Sales Pipeline',
          'Customer Insights',
          'Revenue Growth',
          'Sales Automation',
          'Customer Database',
          'Business Intelligence',
          'Sales Forecasting',
          'Customer Retention'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#059669', 0.15)}`,
                bgcolor: alpha('#059669', 0.05),
                borderColor: alpha('#059669', 0.3),
                color: '#059669'
              }
            }}
          />
        ))}
      </Box>
      
      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <PeopleIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Customer Management',
        description: 'Centralize all customer information with detailed profiles, contact history, and comprehensive relationship tracking for better customer service.',
        color: '#059669',
        items: ['Customer profiles', 'Contact history', 'Relationship tracking', 'Customer insights']
      },
      {
        icon: <ReceiptIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Invoice & Payment Hub',
        description: 'Create professional invoices, track payments, and manage your revenue stream with automated billing and payment tracking systems.',
        color: '#0891b2',
        items: ['Professional invoices', 'Payment tracking', 'Automated billing', 'Revenue management']
      },
      {
        icon: <AnalyticsIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Sales Analytics',
        description: 'Gain valuable insights with comprehensive sales reports, revenue analytics, and performance metrics to drive business growth.',
        color: '#dc2626',
        items: ['Sales reports', 'Revenue analytics', 'Performance metrics', 'Growth insights']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: '#059669' }}>Complete CRM Success</Box>
          </Typography>
        </Box>
        
        {mainFeatures.map((feature, index) => (
          <Box 
            key={index}
            sx={{ 
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{ 
              maxWidth: '1300px', 
              mx: 'auto', 
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: { xs: 6, md: 8 }, 
                          height: { xs: 6, md: 8 }, 
                          borderRadius: '50%', 
                          backgroundColor: '#ffffff', 
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <ContactMailIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Customer Profiles
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Detailed customer information management
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <BusinessIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Business Intelligence
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Smart insights into customer behavior
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 1 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <PaymentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Payment Tracking
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Monitor all payments and invoices
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <CreditCardIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Automated Billing
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Streamlined invoice generation
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 2 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <AssessmentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Sales Reports
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Comprehensive sales performance data
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Growth Metrics
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Track business growth and trends
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* First Image Section - Sales Overview */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<DashboardIcon />}
              label="Sales Dashboard"
              variant="outlined"
              sx={{ 
                borderColor: '#059669',
                color: '#059669',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#059669'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: '#059669' }}>Sales Overview</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Monitor your revenue, track payments, and analyze sales performance in one unified dashboard
          </Typography>
        </Box>

        {/* Sales Dashboard Components - Combined Display */}
        <Box sx={{ 
          maxWidth: '1800px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          {/* Sales Graph */}
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3
          }}>
            <img 
              src={salesGraphImage}
              alt="Revenue Trend Graph"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>

          {/* Recent Payments */}
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3
          }}>
            <img 
              src={recentPaymentsImage}
              alt="Recent Payments"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>

          {/* Outstanding & Overdue Status */}
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={outstandingOverdueImage}
              alt="Outstanding and Overdue Invoices"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>

        {/* Feature Grid Below Image */}
        <Box sx={{ 
          maxWidth: '1200px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: TrendingUpIcon, 
              title: 'Revenue Tracking',
              desc: 'Monitor your business growth with real-time revenue analytics and trends'
            },
            { 
              icon: MonetizationOnIcon, 
              title: 'Payment Status',
              desc: 'Keep track of all payment statuses and outstanding invoices at a glance'
            },
            { 
              icon: AssessmentIcon, 
              title: 'Performance Metrics',
              desc: 'Analyze your sales performance with comprehensive reporting tools'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(5, 150, 105, 0.12)',
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#059669', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: '#059669' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Second Image Section - Invoice Management */}
      <Box sx={{ backgroundColor: alpha('#f8fafc', 0.6), py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<ReceiptIcon />}
              label="Invoice Management"
              variant="outlined"
              sx={{ 
                borderColor: '#0891b2',
                color: '#0891b2',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#0891b2'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Streamlined{' '}
            <Box component="span" sx={{ color: '#0891b2' }}>Invoice Processing</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Create, manage, and track invoices with powerful filtering and comprehensive invoice details
          </Typography>
        </Box>

        {/* Large Image Display */}
        <Box sx={{ 
          maxWidth: '1800px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={invoicePageImage}
              alt="Invoice Management"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>

        {/* Feature List Below Image */}
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 6 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: AssignmentIcon, 
              title: 'Invoice Tracking',
              desc: 'Monitor all your invoices with comprehensive filtering and search capabilities'
            },
            { 
              icon: PaymentIcon, 
              title: 'Payment Status',
              desc: 'Track payment statuses from pending to completed with real-time updates'
            },
            { 
              icon: AccountBalanceIcon, 
              title: 'Financial Overview',
              desc: 'Get insights into your accounts receivable and cash flow management'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%', textAlign: 'center' }}>
              <Box sx={{
                width: { xs: 64, md: 72 },
                height: { xs: 64, md: 72 },
                borderRadius: '50%',
                backgroundColor: alpha('#0891b2', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: { xs: 2, md: 3 },
                border: `2px solid ${alpha('#0891b2', 0.2)}`
              }}>
                <item.icon sx={{ fontSize: { xs: 32, md: 36 }, color: '#0891b2' }} />
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 500,
                color: '#1e293b',
                mb: { xs: 1.5, md: 2 },
                fontSize: { xs: '1.1rem', md: '1.3rem' }
              }}>
                {item.title}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#64748b',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Third Image Section - Invoice Creation */}
      <Box sx={{ py: { xs: 6, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 }, mt: { xs: -6, md: -10 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<DescriptionIcon />}
              label="Invoice Creation"
              variant="outlined"
              sx={{ 
                borderColor: '#dc2626',
                color: '#dc2626',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#dc2626'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Professional{' '}
            <Box component="span" sx={{ color: '#dc2626' }}>Invoice Builder</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Create detailed invoices with customer information, line items, and automated calculations
          </Typography>
        </Box>

        <Box sx={{ 
          maxWidth: '1800px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={invoiceFormImage}
              alt="Invoice Creation Form"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Fourth Image Section - Customer Directory */}
      <Box sx={{ backgroundColor: alpha('#f8fafc', 0.6), py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<GroupIcon />}
              label="Customer Directory"
              variant="outlined"
              sx={{ 
                borderColor: '#7c3aed',
                color: '#7c3aed',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#7c3aed'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: '#7c3aed' }}>Customer Database</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Organize and manage your entire customer base with powerful search and filtering tools
          </Typography>
        </Box>

        <Box sx={{ 
          maxWidth: '1800px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={customerDirectoryImage}
              alt="Customer Directory"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: -4, md: -8 } }}>
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: PeopleIcon, number: '500+', label: 'Happy Customers', color: '#059669' },
            { icon: MonetizationOnIcon, number: '99%', label: 'Payment Success', color: '#0891b2' },
            { icon: TrendingUpIcon, number: '75%', label: 'Revenue Growth', color: '#dc2626' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{ 
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{ 
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#059669',
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to transform your customer relationships?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#bfdbfe'
          }}
        >
          Join successful businesses that have streamlined their CRM processes and accelerated their growth with our comprehensive platform.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: '#059669',
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #059669',
            '&:hover': {
              backgroundColor: '#059669',
              color: '#ffffff'
            }
          }}
        >
          Start Growing Today
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default CRMLandingPage;